// ------------------------------------------------------------------------------
// name: ChunkUploadItem
// author: mudas
// created: 2024-10-15
// ------------------------------------------------------------------------------

import { RcFile } from 'antd/es/upload/interface';
import { ChunkOption, ChunkUploadConfig } from './types';
import { BaseUploader } from './BaseUploader';
import { ChunkUploader } from './ChunkUploader';
import { SingleUploader } from './SingleUploader';
import { FileACL } from 'common/types';
import { AliyunSingleUploader } from './AliyunSingleUploader';

/**
 * 针对单个文件的上传处理器
 */
export class FileUploader {
  private loader?: BaseUploader;
  options?: ChunkOption;

  constructor(
    protected _file: RcFile,
    options?: ChunkOption,
  ) {
    const namespace = options?.params?.namespace;
    if (namespace) {
      this.loader = new AliyunSingleUploader();
    } else {
      this.loader = _file.size > ChunkUploadConfig.chunkThreshold ? new ChunkUploader() : new SingleUploader();
    }
    this.options = options;
  }

  // --------------------------------------------------------------------------
  //
  // Class properties
  //
  // --------------------------------------------------------------------------

  get uid() {
    return this._file.uid;
  }

  get file() {
    return this._file;
  }

  // 是否已暂停上传
  get paused() {
    return !!this.loader?.paused;
  }

  private _loading: boolean = false;

  // 是否正在上传
  get loading() {
    return this._loading;
  }

  private _percent: number = 0;

  // 是否正在上传
  get percent() {
    return this._percent;
  }

  private _completed: boolean = false;

  // 是否上传完成
  get completed() {
    return this._completed;
  }

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  // 创建、管理、调度分片处理器，根据 chunkIndex 切割文件数据交予处理器进行上传
  // 自行请求预签名，创建上传 url，管理上传进度，状态，结果
  // 向上反馈相关状态，进度百分比（依赖回调）
  // 调度级别：文件分片

  // 可以设计策略，决定是调用分片上传，还是整文件上传
  // 当前组件只负责调度传参给“处理器”完整的文件数据，至于其内部实现是整个文件上传还是分片上传，当前组件不必关心

  upload(options?: ChunkOption) {
    //合并options与this.options
    options = { ...this.options, ...options, params: { ...this.options?.params, ...options?.params } };
    const { onFinished, onFailed, onProgress, ...other } = options || {};
    this._loading = true;
    this.loader!.upload(this._file, {
      onFinished: (data, file) => {
        this._completed = true;
        this._loading = false;
        onFinished?.(data, file);
      },
      onFailed: (error) => {
        this._loading = false;
        onFailed?.(error);
      },
      onProgress: (percent, file) => {
        this._percent = percent;
        onProgress?.(percent, file);
      },

      ...other,
    });
  }

  stop() {
    this.loader?.stop();
  }

  resume() {
    this.loader?.resume();
  }
}
